import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useParams, Link } from 'react-router-dom';
import {
  ShoppingCart, ChevronLeft, ChevronRight,
  ArrowLeft, Calendar, Package, Loader2
} from 'lucide-react';

const PRIMARY = '#0099FF';
const ITEMS_PER_PAGE = 20;

const ESTADO_CONFIG = {
  1: { label: 'Abierto', bg: 'bg-amber-100', text: 'text-amber-800' },
  2: { label: 'Confirmado', bg: 'bg-green-100', text: 'text-green-700' },
  3: { label: 'Enviado', bg: 'bg-blue-100', text: 'text-blue-700' },
  4: { label: 'Envío parcial', bg: 'bg-gray-100', text: 'text-gray-600' },
};

const formatCurrency = (value) => {
  if (value == null) return '—';
  return Number(value).toLocaleString('es-ES', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) + ' €';
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const ComercialPedidosCliente = () => {
  const { token, API_URL } = useAuth();
  const { clientId } = useParams();

  const [clientName, setClientName] = useState('');
  const [orders, setOrders] = useState([]);
  const [totalOrders, setTotalOrders] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);

  const totalPages = Math.ceil(totalOrders / ITEMS_PER_PAGE);

  // Fetch client info
  useEffect(() => {
    const fetchClient = async () => {
      try {
        const res = await fetch(`${API_URL}/clients/${clientId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) throw new Error('Error al cargar cliente');
        const data = await res.json();
        const client = data.client || data;
        setClientName(client.razon_social || client.alias || `Cliente #${clientId}`);
      } catch (err) {
        console.error('Error fetching client:', err);
        setClientName(`Cliente #${clientId}`);
      }
    };
    fetchClient();
  }, [API_URL, token, clientId]);

  // Fetch orders
  const fetchOrders = useCallback(async (currentPage = 1) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        cliente: clientId,
      });

      const res = await fetch(`${API_URL}/orders?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al cargar pedidos');
      const data = await res.json();
      setOrders(data.orders || []);
      setTotalOrders(data.pagination?.total || data.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [API_URL, token, clientId]);

  useEffect(() => {
    fetchOrders(page);
  }, [page, fetchOrders]);

  return (
    <div className="h-full overflow-y-auto p-6">

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-full" style={{ backgroundColor: 'rgba(0,153,255,0.08)' }}>
            <ShoppingCart className="w-5 h-5" style={{ color: PRIMARY }} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Pedidos de {clientName}</h1>
            <p className="text-sm text-gray-500">{totalOrders} pedidos en total</p>
          </div>
        </div>
        <Link
          to="/comercial/clientes"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-gray-700 border border-gray-300 hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft size={16} />
          Volver a Clientes
        </Link>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">N° Pedido</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="4" className="px-6 py-12 text-center">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" />
                    <p className="text-sm text-gray-500 mt-2">Cargando pedidos...</p>
                  </td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-12 text-center">
                    <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">Este cliente no tiene pedidos</p>
                  </td>
                </tr>
              ) : (
                orders.map((order) => {
                  const estado = ESTADO_CONFIG[order.estado] || { label: 'Desconocido', bg: 'bg-gray-100', text: 'text-gray-600' };
                  return (
                    <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-900">#{order.npedido || order.id}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-500">
                          <Calendar size={14} className="mr-2 text-gray-400" />
                          {formatDate(order.fecha)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${estado.bg} ${estado.text}`}>
                          {estado.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <span className="text-sm font-medium text-gray-900">{formatCurrency(order.itotaliva ?? order.total)}</span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 bg-gray-50">
            <p className="text-sm text-gray-500">
              Página {page} de {totalPages} · {totalOrders} pedidos
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-lg border border-gray-300 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="text-sm text-gray-700 font-medium px-2">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded-lg border border-gray-300 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComercialPedidosCliente;
